/**
 * URL-backed state for the Explore shell (POS2-880, reworked POS2-1000) —
 * granularity / measure / split / view. The date window lives in the shared
 * `date_from`/`date_to` params (written by the Filters popover); when unset, the
 * window falls back to the granularity's default range (`defaultWindow`). Filters
 * (site/sensor/…) also live in the `useWasteFilters` URL params. Explore-only
 * controls (gran/measure/split/view) live here.
 *
 * All param writes go through one updater per logical change — still the cheapest guard
 * against a half-written state, though no longer load-bearing: `useSearchParamWriter`
 * bases each write on the last one rather than on what the last render saw (POS2-1048).
 */
import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { format } from "date-fns";

import { useSearchParamWriter } from "@/hooks/use-search-param-writer";

import {
  type ExploreWindow,
  type Granularity,
  defaultSpanStart,
  defaultWindow,
  labelSpan,
} from "@/lib/explore-window";
import { zonedDay, zonedDayEnd, zonedDayStart } from "@/lib/zoned-day";

export type Measure = "gbp" | "kg" | "caps";
export type Split = "none" | "dish" | "kitchen" | "service" | "reason";
export type ExploreView = "line" | "bar" | "pie" | "table";

export type ExploreState = {
  gran: Granularity;
  measure: Measure;
  split: Split;
  view: ExploreView;
  window: ExploreWindow;
  windowText: string;
  // Inclusive day bounds ("YYYY-MM-DD") of the current granularity's default window —
  // the Filters popover shows these as placeholders and resets to them (POS2-1000).
  defaultRange: { from: string; to: string };
};

const GRANS: Granularity[] = ["daily", "weekly", "monthly"];
const MEASURES: Measure[] = ["gbp", "kg", "caps"];
const SPLITS: Split[] = ["none", "dish", "kitchen", "service", "reason"];
const VIEWS: ExploreView[] = ["line", "bar", "pie", "table"];

function oneOf<T extends string>(v: string | null, allowed: T[], fallback: T): T {
  return allowed.includes(v as T) ? (v as T) : fallback;
}

// Pie needs a split; fall back to Line when there isn't one.
function resolveView(view: ExploreView, split: Split): ExploreView {
  return view === "pie" && split === "none" ? "line" : view;
}

function setDefaulted(p: URLSearchParams, key: string, value: string, def: string) {
  if (value === def) p.delete(key);
  else p.set(key, value);
}

/** The civil day a bound in the URL stands for.
 *
 * Two writers, two encodings. The Filters popover stores the instant at the VIEWER's local
 * midnight, so its day is recovered in that same zone (POS2-1036). A deep link (POS2-886)
 * writes the bare civil day instead, which is the zone-independent form — the same link
 * copied to a colleague in another country opens the same window, which an instant cannot
 * do. Feeding a bare date through `new Date()` would read it as UTC midnight and lose a day
 * in negative-offset zones, so it passes through untouched. */
const CIVIL_DAY = /^\d{4}-\d{2}-\d{2}$/;

function pickedDay(value: string | null): string | null {
  if (!value) return null;
  return CIVIL_DAY.test(value) ? value : format(new Date(value), "yyyy-MM-dd");
}

/** The last included civil day of a half-open [from, to) window, in `timeZone`. */
function lastDay(to: Date, timeZone: string): string {
  return zonedDay(new Date(to.getTime() - 1), timeZone);
}

/**
 * The window the URL's date bounds describe, or null when neither is set (use the granularity
 * default then). Either bound alone counts (POS2-1037).
 *
 * An open bound still means "this page's default", which is what the popover prints as that
 * field's placeholder — so setting a start date must never move the end the reader can already
 * see (Artem, POS2-1051). The placeholder is styled like a real value, so a silent move reads as
 * the app overwriting their input.
 *
 * The one adjustment is what stops the window **inverting**. `defaultWindow`'s bounds are fixed
 * dates with no relation to what the user gave — `to` is always in the past (start of this month,
 * or start of today) and `from` an arbitrary span ago — so a lone bound on the far side of one
 * produced `from > to`: the header read "Jul 2026 – Jun 2026" and the canvas said "No capture
 * records in this window", which reads as *you have no data* rather than *your range is
 * backwards*. Both directions were reachable.
 *
 * So an open end holds at the default's last day unless the start has passed it, and an open
 * start becomes the granularity's usual span ending at the given end — which cannot invert by
 * construction. A future start date keeps its own day.
 */
function pickedWindow(
  fromDay: string | null,
  toDay: string | null,
  gran: Granularity,
  dflt: ExploreWindow,
  timeZone: string,
): ExploreWindow | null {
  if (!fromDay && !toDay) return null;
  const dfltLast = lastDay(dflt.to, timeZone);
  const last = toDay ?? (fromDay !== null && fromDay > dfltLast ? fromDay : dfltLast);
  const first = fromDay ?? zonedDay(defaultSpanStart(gran, last, timeZone), timeZone);
  return { from: zonedDayStart(first, timeZone), to: zonedDayEnd(last, timeZone) };
}

export function useExploreState(nowArg?: Date, timeZone = "UTC") {
  const [searchParams] = useSearchParams();
  // Fix "now" at mount so the default window doesn't drift across renders.
  // useRef is called unconditionally (rules-of-hooks); the arg is only read once.
  const fallbackNow = useRef(new Date()).current;
  const now = nowArg ?? fallbackNow;

  const state: ExploreState = useMemo(() => {
    const gran = oneOf(searchParams.get("gran"), GRANS, "daily");
    const split = oneOf(searchParams.get("split"), SPLITS, "none");
    const dflt = defaultWindow(gran, now, timeZone);
    // A custom range set in the Filters popover overrides the granularity default. The popover
    // stores each picked bound at the VIEWER's local midnight, so recover the civil day and
    // re-anchor it to the site tz — otherwise the RPC (which buckets in the site tz) drops a
    // day off each end (POS2-1036). `pickedWindow` owns what a single-ended range means.
    const fromDay = pickedDay(searchParams.get("date_from"));
    const toDay = pickedDay(searchParams.get("date_to"));
    const window = pickedWindow(fromDay, toDay, gran, dflt, timeZone) ?? dflt;
    return {
      gran,
      measure: oneOf(searchParams.get("measure"), MEASURES, "gbp"),
      split,
      view: resolveView(oneOf(searchParams.get("view"), VIEWS, "line"), split),
      window,
      windowText: labelSpan(gran, zonedDay(window.from, timeZone), lastDay(window.to, timeZone)),
      defaultRange: {
        from: zonedDay(dflt.from, timeZone),
        to: lastDay(dflt.to, timeZone),
      },
    };
  }, [searchParams, now, timeZone]);

  const { write } = useSearchParamWriter({ replace: true });

  const setGran = useCallback(
    // Granularity changes the bucket size only. A custom range (date_from/date_to) set
    // in the Filters popover is preserved across granularity switches; the per-granularity
    // default window applies only when no custom range is set (clear the dates to return).
    (gran: Granularity) => write((p) => setDefaulted(p, "gran", gran, "daily")),
    [write],
  );

  const setMeasure = useCallback(
    (m: Measure) => write((p) => setDefaulted(p, "measure", m, "gbp")),
    [write],
  );
  const setSplit = useCallback(
    (s: Split) => write((p) => setDefaulted(p, "split", s, "none")),
    [write],
  );
  const setView = useCallback(
    (v: ExploreView) => write((p) => setDefaulted(p, "view", v, "line")),
    [write],
  );

  return { state, setGran, setMeasure, setSplit, setView };
}
